import { useMutation, useQueryClient } from '@tanstack/react-query';

import useAPI from '@/hooks/useAPI';
import CeleryConsoleRepository from '@/repositories/CeleryConsoleRepository';

export const useCeleryOverview = (refetchInterval: number | false = false) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryOverview'],
    queryFn: () => repo.getOverview(),
    refetchInterval
  });
};

export const useCeleryConfig = (refetchInterval: number | false = false) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryConfig'],
    queryFn: () => repo.getConfig(),
    refetchInterval
  });
};

export const useCeleryUnacked = (refetchInterval: number | false = false) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryUnacked'],
    queryFn: () => repo.getUnacked(),
    refetchInterval
  });
};

export const useCeleryActive = (refetchInterval: number | false = false) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryActive'],
    queryFn: () => repo.getActive(),
    refetchInterval
  });
};

export const useRecentTaskStatuses = (limit = 50, refetchInterval: number | false = false) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryRecentTaskStatuses', limit],
    queryFn: () => repo.getRecentTaskStatuses(limit),
    refetchInterval
  });
};

export const useTaskStatusLookup = (id: string) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryTaskStatus', id],
    queryFn: () => repo.getTaskStatus(id),
    enabled: Boolean(id)
  });
};

export const usePeekQueue = (queue: string, limit?: number) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryPeek', queue, limit],
    queryFn: () => repo.peekQueue(queue, limit),
    enabled: Boolean(queue)
  });
};

// On-demand (no polling) — only fires when ``queue`` is set, i.e. when the
// caller opens an expander on the Overview. Sampling the broker on every
// poll would be heavy.
export const useQueueBreakdown = (queue: string | null, perEnd?: number, full?: boolean) => {
  const repo = CeleryConsoleRepository();
  return useAPI({
    queryKey: ['celeryQueueBreakdown', queue, perEnd, full ?? false],
    queryFn: () => repo.getQueueBreakdown(queue ?? '', perEnd, full),
    enabled: Boolean(queue)
  });
};

export const useRestoreUnacked = () => {
  const repo = CeleryConsoleRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryTags: string[]) => repo.restore(deliveryTags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['celeryUnacked'] });
      queryClient.invalidateQueries({ queryKey: ['celeryOverview'] });
    }
  });
};

export const useRemoveUnacked = () => {
  const repo = CeleryConsoleRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryTags: string[]) => repo.remove(deliveryTags),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['celeryUnacked'] });
      queryClient.invalidateQueries({ queryKey: ['celeryOverview'] });
    }
  });
};

export const usePurgeUnacked = () => {
  const repo = CeleryConsoleRepository();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskPath: string) => repo.purgeUnacked(taskPath),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['celeryUnacked'] });
      queryClient.invalidateQueries({ queryKey: ['celeryOverview'] });
    }
  });
};

export const usePurgeQueueByTaskPath = () => {
  const repo = CeleryConsoleRepository();
  return useMutation({
    mutationFn: ({ queue, taskPath }: { queue: string; taskPath: string }) =>
      repo.purgeByTaskPath(queue, taskPath)
    // Intentionally NO onSuccess: callers want to show success UI before the
    // row disappears under them. See QueuesPanel for the deferred-invalidate
    // pattern that gives a visible success-feedback window.
  });
};
